/* ─────────────────────────────────────────────────────────────────────────
   Homepage copy.

   Nothing here is new marketing copy. Every line is either the storyboard's
   own wording or lifted from the component / data file that already carries
   it on the live site (named beside each block), so the homepage and the
   inner pages can never drift apart.
   ───────────────────────────────────────────────────────────────────────── */
import { INDUSTRIES } from '../../data/industriesData';
import { SECTORS } from '../../data/sectorServicesData';
import { PROJECTS } from '../../data/projectsData';

/* Hero — from components/Hero.jsx and components/TabBar.jsx */
export const HERO = {
  tagline: ['ENGINEERING', 'INNOVATION', 'EXCELLENCE'],
  line1: ['CUSTOM ', 'IOT', ' SOLUTIONS'],
  line2: 'FROM PROTOTYPE TO PRODUCTION',
  sub: ['END-TO-END EMBEDDED ', '& IOT SOLUTIONS'],
  steps: ['Sketch the idea', 'Shape the system', 'Scale the impact'],
  cta: { label: 'Get In Touch', to: '/contact' },
};

/* The build story — storyboard wording. `id` doubles as the chapter key the
   3D stage listens for. */
export const STORY = [
  {
    id: 'idea', n: '01', eyebrow: 'The Idea',
    title: 'The idea',
    body: 'Every system starts with a problem worth solving.',
    tags: ['Research', 'Architecture', 'Feasibility'],
  },
  {
    id: 'build', n: '02', eyebrow: 'Build · PCB Assembly',
    title: 'Build',
    body: 'From architecture to working hardware.',
    tags: ['PCB Design', 'Component Sourcing', 'Assembly', 'Firmware'],
  },
  {
    id: 'prototype', n: '03', eyebrow: 'Prototype · 3D Printing',
    title: 'Prototype',
    body: 'Hardware takes shape.',
    tags: ['Mechanical Design', '3D Printing', 'Rapid Prototyping'],
  },
  {
    id: 'integrate', n: '04', eyebrow: 'Integrate · Assembly',
    title: 'Integrate',
    body: 'Hardware. Software. Intelligence.',
    tags: ['System Integration', 'Embedded Software', 'Calibration'],
  },
  {
    id: 'activate', n: '05', eyebrow: 'Activate',
    title: 'Activate',
    body: 'The completed product powers on and starts working.',
    tags: ['Power-on', 'Boot', 'Firmware Live'],
  },
  {
    id: 'validate', n: '06', eyebrow: 'Validate · Testing',
    title: 'Validate',
    body: "Engineering isn't finished when it turns on. It's finished when it works.",
    tags: ['Testing', 'Live Telemetry', 'Validated'],
  },
  {
    id: 'deploy', n: '07', eyebrow: 'Deploy · One Core, Four Industries',
    title: 'One engineering core. Four industries.',
    body: 'The same engineering capability. Different real-world impact.',
    tags: [],
    cta: { label: 'Explore our solutions', to: '/sectors' },
  },
];

/* Industries — straight from data/industriesData.js + data/sectorServicesData.js,
   in the storyboard's order. `product` picks the 3D hero object. */
const ORDER = ['/industries/healthcare', '/industries/industrial-iot', '/industries/education', '/industries/utilities-energy'];
/* Deploy-card taglines — storyboard wording */
const TAGLINE = {
  '/industries/healthcare': 'Better care through technology',
  '/industries/industrial-iot': 'Smarter industries, higher uptime',
  '/industries/education': 'Ideas today, innovators tomorrow',
  '/industries/utilities-energy': 'A sustainable tomorrow',
};
const PRODUCT = {
  '/industries/healthcare': 'health',
  '/industries/industrial-iot': 'gateway',
  '/industries/education': 'rover',
  '/industries/utilities-energy': 'meter',
};
export const INDUSTRY_CHAPTERS = ORDER.map((href, i) => {
  const ind = INDUSTRIES.find(x => x.href === href);
  const sector = SECTORS.find(s => s.to === href);
  return {
    id: `ind${i}`,
    n: `/0${i + 1}`,
    short: ind.short,
    name: ind.name,
    desc: ind.desc,
    tags: ind.tags,
    to: href,
    product: PRODUCT[href],
    tagline: TAGLINE[href],
    services: (sector?.services || []).map(s => ({ title: s.title, to: `${href}/${s.slug}` })),
  };
});

/* Capabilities — storyboard list. Descriptions are the service pages' own
   meta descriptions (pages/*Page.jsx) wherever a page exists. `layer` is the
   slice of the exploded product it lights up. */
export const CAPABILITIES = {
  n: '08',
  eyebrow: 'What We Do',
  title: 'End-to-end engineering for real-world products.',
  body: 'From concept validation to long-term maintenance.',
  items: [
    { k: 'Research & Consulting', layer: 'screen', to: '/research-innovation-rnd',
      d: 'Applied IoT research and product R&D — proof of concept development, technology feasibility studies, TinyML, embedded systems research, and productisation roadmaps.' },
    { k: 'System Architecture', layer: 'lid', to: '/iot-product-design-services',
      d: 'End-to-end hardware design, embedded firmware, cloud integration, and app development for connected products.' },
    { k: 'Electronics Design', layer: 'board', to: '/embedded-hardware-design',
      d: 'Schematic capture, multi-layer PCB layout, RF design, prototype build, and DFM for production.' },
    { k: 'Embedded Systems', layer: 'board', to: '/embedded-firmware-development',
      d: 'RTOS development, OTA updates, low-power optimization, and clean modular firmware architecture.' },
    { k: 'Mechanical Design', layer: 'shell', to: '/iot-product-design-services',
      d: 'Enclosures and mechanics designed around the electronics — from first print to production tooling.' },
    { k: 'Prototyping', layer: 'frame', to: '/iot-product-design-services',
      d: 'From architecture to working hardware — prototype builds you can hold, test and iterate on.' },
    { k: 'Software & Cloud', layer: 'screen', to: '/iot-cloud-integration',
      d: 'AWS IoT, Azure IoT Hub, MQTT pipelines, LoRaWAN networks, secure device provisioning — plus custom dashboards, web applications and mobile apps.' },
    { k: 'Production Deployment', layer: 'base', to: '/iot-product-design-services',
      d: 'From prototype to production — validated, documented and deployed into real operations.' },
    { k: 'Maintenance & Support', layer: 'base', to: '/contact',
      d: 'Long-term care for what we ship: monitoring, OTA updates and support after launch.' },
  ],
  /* components/HowWeWork.jsx */
  process: ['Understanding Your Requirements', 'Research & Planning', 'Design & Development', 'Testing & Deployment'],
};

/* Projects — data/projectsData.js. Problem → Engineering → Result is the
   project's own challenge / approach / outcome. */
export const PROJECT_ARCHIVE = {
  n: '09',
  eyebrow: 'Featured Projects',
  title: 'Real problems. Engineered solutions.',
  items: PROJECTS.slice(0, 8).map(p => ({
    slug: p.slug, code: p.code, title: p.title, tagline: p.tagline,
    sector: p.sectorName, color: p.color, year: p.year, image: p.image,
    problem: p.challenge?.body, engineering: p.approach?.body, result: p.outcome?.body,
    metrics: (p.metrics || []).slice(0, 3), tags: p.tags || [],
  })),
};

/* Proof — Testimonial.jsx, TrustedBy.jsx, EcosystemConnect.jsx */
export const PROOF = {
  n: '10',
  eyebrow: 'Trusted By Innovators',
  title: 'Turning ideas into impact with forward-thinking partners.',
  stats: [
    { value: 4, suffix: '+', label: 'Years' },
    { value: 12, suffix: '+', label: 'Projects shipped' },
    { value: 12, suffix: '', label: 'Team size' },
    { value: 6, suffix: '+', label: 'Industries served' },
    { value: 150, suffix: '+', label: 'Happy clients' },
  ],
  /* components/Testimonial.jsx first, then the client quotes carried by each
     case study in data/projectsData.js */
  testimonials: [
    {
      rating: 4.5,
      quote: 'Working with Zone-X was a great experience. Their team understood our problem clearly, researched the best solution, and delivered the project exactly as we needed. The process was smooth, professional, and the final result exceeded our expectations.',
      name: 'Ajay Ratan', role: 'Product Manager',
    },
    ...PROJECTS.filter(p => p.quote).map(p => ({
      quote: p.quote.text, name: p.quote.author, role: p.quote.role,
      project: p.title, sector: p.sectorName, color: p.color,
    })),
  ],
  clients: ['EXCEL', 'STEON', 'ELPRO', 'OKAMOND', 'AVARTHAN', 'ProDiet', 'Kanti Sweets'],
  ecosystemTitle: ['PLUG ZONE', '-X', ' INTO', 'Your Existing Ecosystem.'],
  ecosystemSub: 'IoT devices & sensors · Cloud infrastructure · Dev toolchains · Analytics · APIs for custom workflows and automation.',
  ecosystem: ['Python', 'AWS', 'MongoDB', 'GitHub', 'Docker', 'Next.js', 'G Cloud', 'Raspberry Pi', 'Arduino', 'Intel CPU', 'Firebase', 'REST API', 'AI / ML', 'Android', 'iOS', 'NVIDIA GPU', 'Bluetooth', 'USB'],
};

/* Final CTA — storyboard */
export const FINAL = {
  n: '11',
  eyebrow: "Let's Engineer It",
  title: 'Have an idea?',
  accent: "Let's build what's next.",
  cta: { label: 'Start a project', to: '/contact' },
  trail: ['Ideas', 'Engineering', 'Real-world impact'],
};
